// Schemas Zod para validação de eventos, participantes e reuniões

import { z } from 'zod';
import { MEETING_STATUS, PARTICIPANT_TYPE, PRIORITY_LEVELS } from '../constants/status';
import { UserSchema } from './user.schemas';

// Meeting Status
export const MeetingStatusSchema = z.enum(['Pendente', 'Realizado', 'No-show', 'Cancelado', 'Reagendar']);

// Participant Type
export const ParticipantTypeSchema = z.enum(['FABRICANTE', 'REVENDA']);

// Priority Level
export const PriorityLevelSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]);

// Intervalo Evento Schema
export const IntervaloEventoSchema = z.object({
  id: z.string().uuid(),
  inicio: z.string().regex(/^\d{2}:\d{2}$/, 'Horário deve estar no formato HH:mm'),
  fim: z.string().regex(/^\d{2}:\d{2}$/, 'Horário deve estar no formato HH:mm'),
  descricao: z.string()
});

// Dia Evento Schema
export const DiaEventoSchema = z.object({
  id: z.string().uuid(),
  data: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD'),
  inicio: z.string().datetime(),
  fim: z.string().datetime(),
  slotDurationMin: z.number().min(15).max(120),
  slotIntervalDurationMin: z.number().min(5).max(60),
  intervalos: z.array(IntervaloEventoSchema).optional()
});

// Mesa posicionada sobre a planta do evento (x e y em % da imagem)
export const MesaMapaSchema = z.object({
  numero: z.number().int().min(1, 'Número da mesa deve ser maior que zero').max(999, 'Número da mesa deve ser no máximo 999'),
  x: z.number().min(0).max(100),
  y: z.number().min(0).max(100)
});

export const MapaMesasSchema = z.array(MesaMapaSchema).refine(
  mesas => new Set(mesas.map(mesa => mesa.numero)).size === mesas.length,
  'Cada mesa só pode aparecer uma vez no mapa'
);

// Event Schema
export const EventSchema = z.object({
  id: z.string().uuid(),
  nome: z.string().min(3, 'Nome do evento deve ter no mínimo 3 caracteres'),
  diasEvento: z.array(DiaEventoSchema).optional(),
  floorPlanUrl: z.string().url().optional(),
  mapaMesas: MapaMesasSchema.optional(),
  brand: z.object({
    logoUrl: z.string().url().optional(),
    primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Cor primária deve ser um hex válido'),
    secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Cor secundária deve ser um hex válido'),
    accentColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    fontFamily: z.string().optional(),
    customCss: z.string().optional()
  }),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

// Participant Schema
export const ParticipantSchema = z.object({
  id: z.string().uuid(),
  tipo: ParticipantTypeSchema,
  empresa: z.string().min(2, 'Nome da empresa deve ter no mínimo 2 caracteres'),
  representante: z.array(UserSchema),
  eventId: z.string().uuid(),
  mesa: z.number().min(1).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

// Relationship Schema
export const RelationshipSchema = z.object({
  id: z.string().uuid(),
  fabricanteId: z.string().uuid(),
  revendaId: z.string().uuid().optional(),
  prioridade: PriorityLevelSchema,
  eventId: z.string().uuid(),
  observacoes: z.string().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

// Meeting Schema
export const MeetingSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  fabricanteId: z.string().uuid(),
  revendaId: z.string().uuid().optional(),
  fabricante: z.lazy(() => ParticipantSchema).optional(),
  revenda: z.lazy(() => ParticipantSchema).optional(),
  inicio: z.string().datetime(),
  fim: z.string().datetime(),
  mesa: z.number().min(1).optional(),
  status: MeetingStatusSchema,
  resultadoNota: z.string().optional(),
  observacoes: z.string().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

// Agenda Conflict Schema
export const AgendaConflictSchema = z.object({
  id: z.string().uuid(),
  meetingId: z.string().uuid(),
  tipo: z.enum(['SOBREPOSICAO_FABRICANTE', 'SOBREPOSICAO_REVENDA', 'MESA_OCUPADA', 'HORARIO_INDISPONIVEL']),
  descricao: z.string(),
  severidade: z.enum(['ALTA', 'MEDIA', 'BAIXA']),
  resolvido: z.boolean(),
  createdAt: z.string().datetime()
});

// Agenda Stats Schema
export const AgendaStatsSchema = z.object({
  totalMeetings: z.number().min(0),
  meetingsPorStatus: z.record(MeetingStatusSchema, z.number().min(0)),
  conflitosAtivos: z.number().min(0),
  taxaNoShow: z.number().min(0).max(100),
  participantesComAgenda: z.number().min(0),
  horariosDisponiveis: z.number().min(0),
  duracaoGeracaoMs: z.number().min(0).optional()
});

// Filter Options Schema
export const FilterOptionsSchema = z.object({
  dataInicio: z.string().datetime().optional(),
  dataFim: z.string().datetime().optional(),
  status: z.array(MeetingStatusSchema).optional(),
  fabricantes: z.array(z.string().uuid()).optional(),
  revendas: z.array(z.string().uuid()).optional(),
  mesas: z.array(z.number().min(1)).optional()
});

// Schemas para criação/atualização
export const CreateEventSchema = EventSchema.omit({ id: true, createdAt: true, updatedAt: true });
export const UpdateEventSchema = CreateEventSchema.partial();

export const CreateParticipantSchema = ParticipantSchema.omit({ id: true, createdAt: true, updatedAt: true });
export const UpdateParticipantSchema = CreateParticipantSchema.partial();

export const CreateMeetingSchema = MeetingSchema.omit({ id: true, createdAt: true, updatedAt: true, fabricante: true, revenda: true });
export const UpdateMeetingSchema = CreateMeetingSchema.partial();

export const CreateAgendaConflictSchema = AgendaConflictSchema.omit({ id: true, createdAt: true });
export const UpdateAgendaConflictSchema = CreateAgendaConflictSchema.partial();

// Export types
export type EventType = z.infer<typeof EventSchema>;
export type DiaEventoType = z.infer<typeof DiaEventoSchema>;
export type IntervaloEventoType = z.infer<typeof IntervaloEventoSchema>;
export type ParticipantType = z.infer<typeof ParticipantSchema>;
export type RelationshipType = z.infer<typeof RelationshipSchema>;
export type MeetingType = z.infer<typeof MeetingSchema>;
export type AgendaConflictType = z.infer<typeof AgendaConflictSchema>;
export type AgendaStatsType = z.infer<typeof AgendaStatsSchema>;
export type FilterOptionsType = z.infer<typeof FilterOptionsSchema>;

export type CreateEventType = z.infer<typeof CreateEventSchema>;
export type UpdateEventType = z.infer<typeof UpdateEventSchema>;
export type CreateParticipantType = z.infer<typeof CreateParticipantSchema>;
export type UpdateParticipantType = z.infer<typeof UpdateParticipantSchema>;
export type CreateMeetingType = z.infer<typeof CreateMeetingSchema>;
export type UpdateMeetingType = z.infer<typeof UpdateMeetingSchema>;
export type CreateAgendaConflictType = z.infer<typeof CreateAgendaConflictSchema>;
export type UpdateAgendaConflictType = z.infer<typeof UpdateAgendaConflictSchema>;